import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { CustomDelete } from 'components';
import { appointmentActions, httpRequestsOnErrorsActions, httpRequestsOnLoadActions, httpRequestsOnSuccessActions } from 'store';
import { FindErrorItem, FindSuccessItem, PermissionList, RolePermission, useModal, User, Images } from 'utils';
import { appointmentService } from '../../../store/appointment/appointment.service';
import { apptEnums, checkEditPermission } from './constants';
import { changeDateTime } from './modals/constants';
import { SERVICE } from '../constants';
import warning from "../../../assets/images/icons/warning.svg";

const EDIT_TYPE = 'EDIT_APPOINTMENT';

/** Why a dropped appt can't be moved, or null when it can. Mirrors the server-side rules so the
 * common cases fail fast in a modal instead of round-tripping. */
const getMoveError = (appt, start, end, userInfo) => {
    if (!checkEditPermission(appt, userInfo)) return 'You do not have permission to edit this appointment.';
    if (appt?.status !== apptEnums.PENDING) {
        return `Only pending appointments can be moved. This appointment is ${appt?.status?.toLowerCase()}.`;
    }
    if (appt?.locked && !RolePermission([PermissionList.LOCKED_APPT_EDIT?.code])) {
        return 'This appointment is locked. Unlock it before changing its time.';
    }
    // times are stored as HH:mm on startDate, so an appt can't run past midnight
    if (!moment(start).isSame(end, 'day')) return 'An appointment must start and end on the same day.';
    if (!moment(end).isAfter(start)) return 'The end time must be after the start time.';
    return null;
};

const toId = (value) => (value && typeof value === 'object' ? value.id || value._id : value) || undefined;

const buildEditBody = (appt, start, end) => {
    const startDate = moment(start).format('YYYY-MM-DD');
    const body = {
        type: appt?.type,
        staff: toId(appt?.staff),
        staffApptType: appt?.staffApptType,
        subject: appt?.subject,
        payCodeType: appt?.payCodeTypeId,
        miles: appt?.miles ? +appt.miles : 0,
        location: appt?.location || null,
        telehealth: appt?.telehealth,
        startAddress: appt?.startAddress,
        endAddress: appt?.endAddress,
        creationNote: appt?.creationNote,
        supervision: appt?.supervision || false,
        supervisee: appt?.supervision ? toId(appt?.supervisee) || null : null,
        editTemplate: false,
        startDate,
        startTime: changeDateTime(startDate, moment(start).format('HH:mm')),
        endTime: changeDateTime(startDate, moment(end).format('HH:mm')),
    };
    if (appt?.type === SERVICE) {
        body.client = toId(appt?.client);
        body.authorizedService = toId(appt?.authorizedService);
        body.placeService = appt?.posId;
        body.chargeRate = appt?.chargeRateId || null;
        body.requireSignature = appt?.requireSignature;
        body.requireProviderSignature = appt?.requireProviderSignature;
        body.requireNote = appt?.requireNote;
        if (appt?.serviceType) body.serviceType = appt.serviceType;
    }
    return body;
};


export const useApptReschedule = (appointments) => {
    const dispatch = useDispatch();
    const { open, close } = useModal();
    const { userInfo } = User();
    const [pendingMove, setPendingMove] = useState(null);
    const moving = useRef(false);
    const backError = FindErrorItem(EDIT_TYPE);
    const success = FindSuccessItem(EDIT_TYPE);

    const showError = (text) => {
        open(
            <CustomDelete
                info="Unable to Move Appointment"
                okBtn={true}
                body={
                    <div className='appt-warning-info-box flex-align-center' style={{gap: 12}}>
                        <img src={Images.warning} alt="warningInfo"/>
                        <p>{text}</p>
                    </div>
                }
            />
        );
    };

    const fail = (message) => {
        moving.current = false;
        setPendingMove(null);
        showError(typeof message === 'string' && message ? message : 'Could not move the appointment.');
    };

    useEffect(() => {
        if (backError && moving.current) {
            fail(Array.isArray(backError?.error) ? backError.error.join(', ') : backError?.error);
            dispatch(httpRequestsOnErrorsActions.removeError(EDIT_TYPE));
        }
    }, [backError]);

    // Our success must not linger in the store: CreateEvent (Edit modal) closes itself on mount if it
    // finds an EDIT_APPOINTMENT success. The group confirm (CustomDelete) also clears it — harmless.
    useEffect(() => {
        if (success && moving.current) {
            moving.current = false;
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_TYPE));
        }
    }, [success]);

    // keep the optimistic position until the refetched list lands (not mid-request)
    useEffect(() => {
        if (!moving.current) setPendingMove(null);
    }, [appointments]);

    const submit = async (event, start, end) => {
        if (moving.current) return;
        moving.current = true;
        setPendingMove({ id: event?.id, start, end });
        dispatch(httpRequestsOnLoadActions.appendLoading(EDIT_TYPE));
        try {
            const res = await appointmentService.getAppointmentByIdService(event?.id);
            dispatch(appointmentActions.editAppointment(buildEditBody(res?.data, start, end), event?.id));
        } catch (err) {
            dispatch(httpRequestsOnLoadActions.removeLoading(EDIT_TYPE));
            fail(err?.data?.message);
        }
    };

    const handleMove = ({ event, start, end, isAllDay }) => {
        if (isAllDay) return;
        if (moment(start).isSame(event?.start) && moment(end).isSame(event?.end)) return;

        const error = getMoveError(event, start, end, userInfo);
        if (error) return showError(error);

        if (event?.template) {
            const from = `${moment(event?.start).format('MM/DD/YYYY h:mm A')} - ${moment(event?.end).format('h:mm A')}`;
            const to = `${moment(start).format('MM/DD/YYYY h:mm A')} - ${moment(end).format('h:mm A')}`;
            open(
                <CustomDelete
                    info="Move Appointment from Group"
                    text={`This appointment is part of a recurring group. Moving it from ${from} to ${to} will disconnect it from the group. The other appointments in the group will not change.`}
                    innerText="Move & Disconnect"
                    color="blue"
                    actionType={EDIT_TYPE}
                    handleDel={() => submit(event, start, end)}
                    handleClose={close}
                />
            );
            return;
        }

        submit(event, start, end);
    };

    /** overlay the optimistic position on the calendar events */
    const applyPendingMove = (events = []) =>
        pendingMove ? events.map((e) => (e?.id === pendingMove.id ? { ...e, start: pendingMove.start, end: pendingMove.end } : e)) : events;

    const canDrag = (event) => checkEditPermission(event, userInfo);

    return { handleMove, applyPendingMove, canDrag };
};
